// AI-generated with Claude Code.
import { useState } from 'react'
import type { Member } from '../../shared/types'
import { normaliseUsername, usernameProblem } from '../../shared/auth'
import { api } from '../lib/api'
import { useRefreshingMutation } from '../lib/queries'
import { ErrorText, MIN_PASSWORD_LENGTH, PasswordHint } from './ui'

/**
 * A parent sets the username and password a kid signs in with. Changing the
 * password signs the kid out everywhere, so it doubles as "lost my tablet".
 */
export function KidLoginForm({ member }: { member: Member }) {
  // The sheet holds a snapshot of the member, so track the saved login here.
  const [savedUsername, setSavedUsername] = useState(member.username)
  const [username, setUsername] = useState(member.username ?? '')
  const [password, setPassword] = useState('')
  const [notice, setNotice] = useState<string | null>(null)

  const save = useRefreshingMutation(() =>
    api.setMemberLogin(member.id, { username: normaliseUsername(username), password: password || undefined }),
  )
  const remove = useRefreshingMutation(() => api.removeMemberLogin(member.id))

  const hasLogin = savedUsername !== null
  const typedUsername = normaliseUsername(username)
  const problem = typedUsername ? usernameProblem(typedUsername) : null
  const passwordReady = password ? password.length >= MIN_PASSWORD_LENGTH : hasLogin
  const changed = typedUsername !== savedUsername || password.length > 0
  const canSave = typedUsername.length > 0 && !problem && passwordReady && changed && !save.isPending

  return (
    <div className="stack">
      <p className="section-title">Login</p>
      <p className="faint">
        {hasLogin
          ? `${member.name} signs in as “${savedUsername}”.`
          : `${member.name} can't sign in yet. Choose a username and password for them.`}
      </p>

      <div className="field">
        <label className="label" htmlFor="kid-username">
          Username
        </label>
        <input
          id="kid-username"
          className="input"
          autoCapitalize="none"
          autoComplete="off"
          spellCheck={false}
          value={username}
          onChange={(event) => setUsername(event.target.value)}
        />
        {problem ? <span className="error-text">{problem}</span> : null}
      </div>

      <div className="field">
        <label className="label" htmlFor="kid-password">
          {hasLogin ? 'New password (leave blank to keep it)' : 'Password'}
        </label>
        <input
          id="kid-password"
          className="input"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
        {password || !hasLogin ? <PasswordHint password={password} /> : null}
      </div>

      <ErrorText error={save.error ?? remove.error} />
      {notice ? <p className="faint">{notice}</p> : null}

      <button
        type="button"
        className="btn btn-ghost btn-block"
        disabled={!canSave}
        onClick={() =>
          save.mutate(undefined, {
            onSuccess: (updated) => {
              setSavedUsername(updated.username)
              setUsername(updated.username ?? '')
              setNotice(password ? `Saved. ${member.name} has been signed out everywhere.` : 'Saved.')
              setPassword('')
            },
          })
        }
      >
        Save login
      </button>

      {hasLogin ? (
        <button
          type="button"
          className="btn btn-quiet btn-block"
          disabled={remove.isPending}
          onClick={() =>
            remove.mutate(undefined, {
              onSuccess: () => {
                setSavedUsername(null)
                setUsername('')
                setPassword('')
                setNotice(`Login removed. ${member.name} has been signed out everywhere.`)
              },
            })
          }
        >
          Remove login
        </button>
      ) : null}
    </div>
  )
}
